import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import SearchableSelect from '@/temu-platform/modules/products/components/SearchableSelect';
import {
  isIllegalTemuTryOnSizeName,
  mapModelTemplateToGoodsModelReq,
  normalizeTemuTryOnResult,
  resolveGoodsModelTryOnSize,
  TEMU_TRY_ON_RESULT_OPTIONS,
  TEMU_TRY_ON_RESULT_TRUE_TO_SIZE,
} from '@/temu-platform/utils/temuOfficialSizeChart';
import { temuPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';

/** 平台 modelType：0 成衣 / 1 鞋类 */
const MODEL_TYPE_CLOTHES = '0';
const MODEL_TYPE_SHOE = '1';

function normalizeLockedModelType(raw) {
  const t = String(raw ?? MODEL_TYPE_CLOTHES).trim();
  return t === MODEL_TYPE_SHOE || t === '2' ? MODEL_TYPE_SHOE : MODEL_TYPE_CLOTHES;
}

function modelTypeName(typeKey) {
  return typeKey === MODEL_TYPE_SHOE ? '鞋类模特' : '成衣模特';
}

function modelRowId(row) {
  return String(row?.id || row?.modelId || row?.templateId || row?.platformModelId || '').trim();
}

function modelDisplayName(row) {
  return String(row?.name || row?.modelName || row?.payload?.modelName || '').trim()
    || (modelRowId(row) ? `模特#${modelRowId(row)}` : fixedUi("模特"));
}

function resolveRowModelType(row) {
  const t = row?.modelType ?? row?.payload?.modelType;
  if (t === 1 || t === '1' || t === 2 || t === '2') return '1';
  if (t === 0 || t === '0') return '0';
  return '';
}

/**
 * 发品 goodsModelReqs.modelType：1 成衣 / 2 鞋类（与模特库 0/1 不同）。
 * 返回与 lockedModelType 同一套键：'0' 成衣 / '1' 鞋类。
 */
function resolveGoodsModelReqTypeKey(req) {
  const t = Number(req?.modelType);
  if (t === 2) return MODEL_TYPE_SHOE;
  if (t === 1) return MODEL_TYPE_CLOTHES;
  // 兜底：若仍是库表编码 0/1
  return resolveRowModelType(req);
}

function modelTypeLabel(row) {
  if (row?.modelTypeLabel) return String(row.modelTypeLabel);
  const t = resolveRowModelType(row);
  if (t === '0') return '成衣模特';
  if (t === '1') return '鞋类模特';
  return '';
}

function preferReachableImageUrl(...candidates) {
  const urls = candidates.map((value) => String(value || '').trim()).filter(Boolean);
  return urls.find((url) => /^https?:\/\//i.test(url)) || urls[0] || '';
}

function modelThumb(row) {
  return preferReachableImageUrl(
    row?.thumbUrl,
    row?.modelProfileUrl,
    row?.headPortrait,
    row?.payload?.thumbUrl,
    row?.payload?.modelProfileUrl,
    row?.payload?.headPortrait,
  );
}

function measureText(reqOrRow = {}) {
  const clothes = reqOrRow.clothesModel || reqOrRow.payload?.clothesModel || {};
  const shoe = reqOrRow.shoeModel || reqOrRow.payload?.shoeModel || {};
  return [
    ['身高', reqOrRow.modelHeight ?? reqOrRow.height ?? clothes.height],
    ['胸围', reqOrRow.modelBust ?? reqOrRow.bust ?? clothes.bust],
    ['腰围', reqOrRow.modelWaist ?? reqOrRow.waist ?? clothes.waist],
    ['臀围', reqOrRow.modelHip ?? reqOrRow.hip ?? clothes.hipline],
    ['脚长', reqOrRow.modelFootLength ?? reqOrRow.footLength ?? shoe.footLength],
    ['脚宽', reqOrRow.modelFootWidth ?? reqOrRow.footWidth ?? shoe.footWidth],
  ]
    .filter(([, v]) => v != null && String(v).trim() !== '')
    .map(([k, v]) => `${k} ${v}`);
}

function applyTryOnSize(req, sizeOpt) {
  if (!req || !sizeOpt) return req;
  const resolved = resolveGoodsModelTryOnSize(sizeOpt.name || sizeOpt.label, [sizeOpt]);
  if (!resolved) return req;
  return {
    ...req,
    sizeSpecId: resolved.sizeSpecId,
    sizeSpecName: resolved.sizeSpecName,
    sizeValueId: resolved.sizeValueId,
  };
}

/**
 * 发品 / 产品模板选择模特。
 * - 店铺：仅可选已同步到店铺的平台模特
 * - 官方模板（officialMode）：可选官方模特库（店铺套用时再匹配本店模特）
 * 模特类型由类目锁定（成衣 / 鞋类）。
 */
export default function TemuModelSelectBar({
  shopId = '',
  officialMode = false,
  shopType = '',
  visible = true,
  /** 叶子类目命中平台「部分类目模特必填」清单时为 true */
  required = false,
  /**
   * 类目锁定的模特类型：0 成衣 / 1 鞋类。
   * 服装类目固定成衣；鞋类尺码表类目固定鞋类。
   */
  modelType = MODEL_TYPE_CLOTHES,
  value = [],
  onChange,
  errorMessage = '',
  /** @type {Array<{ id: string, name: string, specId?: number }>} */
  sizeOptions = [],
}) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const lockedModelType = normalizeLockedModelType(modelType);
  const lockedModelTypeName = modelTypeName(lockedModelType);

  useEffect(() => {
    if (!visible) {
      setCatalog([]);
      return undefined;
    }
    if (officialMode) {
      if (!shopType) {
        setCatalog([]);
        return undefined;
      }
    } else if (!shopId) {
      setCatalog([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        let items = [];
        if (officialMode) {
          const res = await adminAPI.listOfficialTemuModelTemplates({
            shopType,
            page: 1,
            pageSize: 200,
          });
          const raw = Array.isArray(res?.items) ? res.items : [];
          const want = Number(lockedModelType);
          items = raw.filter((row) => {
            const t = resolveRowModelType(row);
            if (!t) return true;
            return Number(t) === want;
          });
        } else {
          const res = await temuPlatformAPI.listShopModels(shopId, {
            page: 1,
            pageSize: 200,
            modelType: Number(lockedModelType),
          });
          items = Array.isArray(res?.items) ? res.items : [];
        }
        if (cancelled) return;
        setCatalog(items.filter((row) => modelRowId(row)));
      } catch (e) {
        if (!cancelled) {
          setCatalog([]);
          setLoadError(e?.message || '模特列表加载失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, officialMode, shopType, visible, lockedModelType]);

  const catalogById = useMemo(() => {
    const map = new Map();
    catalog.forEach((row) => map.set(modelRowId(row), row));
    return map;
  }, [catalog]);

  const normalizedSizeOptions = useMemo(() => (
    (Array.isArray(sizeOptions) ? sizeOptions : [])
      .map((opt) => ({
        id: String(opt?.id ?? '').trim(),
        name: String(opt?.name || opt?.label || '').trim(),
        specId: Number(opt?.specId || opt?.id || 0) || 0,
      }))
      .filter((opt) => opt.id && opt.name && opt.specId > 0)
  ), [sizeOptions]);

  const sizeSelectOptions = useMemo(() => (
    normalizedSizeOptions.map((opt) => ({
      value: opt.id,
      label: opt.name,
    }))
  ), [normalizedSizeOptions]);

  const options = useMemo(() => {
    const list = catalog.map((row) => {
      const id = modelRowId(row);
      const name = modelDisplayName(row);
      return {
        value: id,
        label: name,
      };
    });
    const first = Array.isArray(value) && value.length ? value[0] : null;
    const savedId = String(first?._templateId || first?.templateId || first?.modelId || '').trim();
    const savedName = String(first?._templateName || first?.modelName || '').trim();
    if (savedId && savedName && !list.some((opt) => opt.value === savedId)) {
      list.unshift({ value: savedId, label: savedName });
    }
    return list;
  }, [catalog, value]);

  const selectedId = useMemo(() => {
    const first = Array.isArray(value) && value.length ? value[0] : null;
    if (!first) return '';
    const candidates = [first._templateId, first.templateId, first.modelId, first.id]
      .map((item) => String(item ?? '').trim())
      .filter(Boolean);
    const matched = candidates.find((id) => catalogById.has(id));
    if (matched) return matched;
    const name = String(first._templateName || first.modelName || '').trim();
    if (name) {
      const byName = catalog.find((row) => modelDisplayName(row) === name);
      if (byName) return modelRowId(byName);
    }
    return candidates[0] || '';
  }, [value, catalogById, catalog]);

  // 仅在类目锁定类型切换时清空；勿用库表 0/1 去比对 goodsModelReq 的 1/2，否则成衣选中会被当成鞋类清掉
  const prevLockedModelTypeRef = useRef(lockedModelType);
  useEffect(() => {
    const prev = prevLockedModelTypeRef.current;
    prevLockedModelTypeRef.current = lockedModelType;
    if (prev === lockedModelType) return;
    const first = Array.isArray(value) && value.length ? value[0] : null;
    if (!first) return;
    const selectedKey = resolveGoodsModelReqTypeKey(first);
    if (selectedKey && selectedKey !== lockedModelType) {
      onChange?.([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 只响应类目锁定类型切换
  }, [lockedModelType]);

  const selectedRow = catalogById.get(selectedId) || null;
  const selectedReq = Array.isArray(value) && value.length ? value[0] : null;

  // 已选模特但试穿尺码非法/缺失时，按商品尺码自动纠正（仅能唯一匹配时）；试穿感受缺省合身
  const selectedSizeSpecId = Number(selectedReq?.sizeSpecId || 0);
  const selectedSizeSpecName = String(selectedReq?.sizeSpecName || '');
  const selectedSizeValueId = String(selectedReq?.sizeValueId || '');
  const selectedTryOnResult = selectedReq
    ? normalizeTemuTryOnResult(selectedReq.tryOnResult, TEMU_TRY_ON_RESULT_TRUE_TO_SIZE)
    : 0;
  useEffect(() => {
    if (!selectedReq) return;
    let next = null;
    const needFeel = !(selectedTryOnResult === 1 || selectedTryOnResult === 2 || selectedTryOnResult === 3);
    if (needFeel) {
      next = { ...(next || selectedReq), tryOnResult: TEMU_TRY_ON_RESULT_TRUE_TO_SIZE };
    }
    if (normalizedSizeOptions.length) {
      const illegal = isIllegalTemuTryOnSizeName(selectedSizeSpecName)
        || !selectedSizeSpecName
        || !(selectedSizeSpecId > 0);
      let sizeStillValid = false;
      if (!illegal) {
        sizeStillValid = normalizedSizeOptions.some((opt) => (
          Number(opt.specId) === selectedSizeSpecId
          || String(opt.id) === selectedSizeValueId
        ));
      }
      if (!sizeStillValid) {
        const resolved = resolveGoodsModelTryOnSize(selectedSizeSpecName, normalizedSizeOptions);
        if (
          resolved
          && !(
            selectedSizeSpecId === resolved.sizeSpecId
            && selectedSizeSpecName === resolved.sizeSpecName
            && String(selectedSizeValueId || '') === String(resolved.sizeValueId || '')
          )
        ) {
          next = {
            ...(next || selectedReq),
            sizeSpecId: resolved.sizeSpecId,
            sizeSpecName: resolved.sizeSpecName,
            sizeValueId: resolved.sizeValueId,
          };
        }
      }
    }
    if (next) onChange?.([next]);
    // 勿依赖 selectedReq 对象引用：onChange 每次都会换新对象，会把自己打进死循环
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随尺码/感受字段变化纠正
  }, [
    selectedSizeSpecId,
    selectedSizeSpecName,
    selectedSizeValueId,
    selectedTryOnResult,
    normalizedSizeOptions,
  ]);

  const applySelectedModel = (row) => {
    const modelId = modelRowId(row);
    let req = mapModelTemplateToGoodsModelReq(row, normalizedSizeOptions);
    if ((!req.sizeSpecId || isIllegalTemuTryOnSizeName(req.sizeSpecName))
      && normalizedSizeOptions.length === 1) {
      req = applyTryOnSize(req, normalizedSizeOptions[0]);
    }
    onChange?.([{
      ...req,
      _templateId: modelId || req._templateId,
      _templateName: modelDisplayName(row) || req._templateName,
    }]);
  };

  const officialHydratedRef = useRef('');
  useEffect(() => {
    const first = Array.isArray(value) && value.length ? value[0] : null;
    if (!first || typeof onChange !== 'function') return undefined;
    const profile = String(first.modelProfileUrl || '').trim();
    const hasHttp = /^https?:\/\//i.test(profile);
    const hasMeasures = first.modelHeight || first.modelBust || first.modelWaist
      || first.modelHip || first.modelFootLength;
    if (hasHttp && hasMeasures) return undefined;
    const portrait = String(first._portraitIpe || profile);
    const officialId = String(
      first._templateId
      || (officialMode ? first.modelId : '')
      || (portrait.includes('/uploads/') ? first.modelId : '')
      || '',
    ).trim();
    if (!officialId || officialHydratedRef.current === officialId) return undefined;
    if (!officialMode && catalogById.has(officialId)) return undefined;
    officialHydratedRef.current = officialId;
    let cancelled = false;
    adminAPI.getOfficialTemuModelTemplate(officialId)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item;
        if (!item) return;
        const req = mapModelTemplateToGoodsModelReq(item, normalizedSizeOptions);
        onChange?.([{
          ...req,
          modelName: first.modelName || req.modelName,
          modelProfileUrl: hasHttp ? profile : (req.modelProfileUrl || profile),
          modelHeight: first.modelHeight || req.modelHeight,
          modelBust: first.modelBust || req.modelBust,
          modelWaist: first.modelWaist || req.modelWaist,
          modelHip: first.modelHip || req.modelHip,
          modelFootLength: first.modelFootLength || req.modelFootLength,
          modelFootWidth: first.modelFootWidth || req.modelFootWidth,
          _templateId: String(item.id || officialId),
          _templateName: first._templateName || req._templateName || first.modelName,
          _portraitIpe: first._portraitIpe || req._portraitIpe,
          sizeSpecId: first.sizeSpecId || req.sizeSpecId,
          sizeSpecName: first.sizeSpecName || req.sizeSpecName,
          sizeValueId: first.sizeValueId || req.sizeValueId,
          tryOnResult: first.tryOnResult ?? req.tryOnResult,
          modelId: first.modelId || req.modelId,
        }]);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // 只在已存模特缺头像或量体时补一次官方资料
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officialMode, value, catalogById]);

  const onSelect = (id) => {
    if (!id) {
      onChange?.([]);
      return;
    }
    const row = catalogById.get(String(id));
    if (!row) {
      onChange?.([]);
      return;
    }
    const hasMeasures = row.height || row.bust || row.footLength
      || row.payload?.clothesModel
      || row.payload?.shoeModel
      || row.clothesModel
      || row.shoeModel;
    if (!officialMode || hasMeasures) {
      applySelectedModel(row);
      return;
    }
    adminAPI.getOfficialTemuModelTemplate(id)
      .then((res) => applySelectedModel(res?.item || row))
      .catch(() => applySelectedModel(row));
  };

  const onTryOnSizeChange = (sizeValueId) => {
    if (!selectedReq) return;
    if (!sizeValueId) {
      const next = { ...selectedReq };
      delete next.sizeSpecId;
      delete next.sizeSpecName;
      delete next.sizeValueId;
      onChange?.([next]);
      return;
    }
    const opt = normalizedSizeOptions.find((row) => String(row.id) === String(sizeValueId));
    if (!opt) return;
    onChange?.([applyTryOnSize(selectedReq, opt)]);
  };

  const onTryOnResultChange = (raw) => {
    if (!selectedReq) return;
    onChange?.([{
      ...selectedReq,
      tryOnResult: normalizeTemuTryOnResult(raw),
    }]);
  };

  const tryOnFeelOptions = useMemo(() => (
    TEMU_TRY_ON_RESULT_OPTIONS.map((opt) => ({
      value: String(opt.value),
      label: opt.label,
    }))
  ), []);

  const cardMeasures = useMemo(() => {
    if (selectedReq) return measureText(selectedReq);
    if (selectedRow) return measureText(selectedRow);
    return [];
  }, [selectedReq, selectedRow]);

  const cardThumb = preferReachableImageUrl(
    selectedRow ? modelThumb(selectedRow) : '',
    selectedReq?.modelProfileUrl,
    selectedReq?._portraitIpe,
  );
  const cardName = selectedReq?.modelName
    || (selectedRow ? modelDisplayName(selectedRow) : '');
  const cardType = selectedRow
    ? (modelTypeLabel(selectedRow) || lockedModelTypeName)
    : (selectedReq ? lockedModelTypeName : '');

  const tryOnSizeValue = String(
    selectedReq?.sizeValueId
    || normalizedSizeOptions.find((opt) => Number(opt.specId) === Number(selectedReq?.sizeSpecId || 0))?.id
    || '',
  );
  const tryOnFeelValue = String(
    normalizeTemuTryOnResult(selectedReq?.tryOnResult),
  );
  const tryOnFeelLabel = TEMU_TRY_ON_RESULT_OPTIONS
    .find((opt) => opt.value === Number(tryOnFeelValue))?.label || '';

  if (!visible) return null;

  return (
    <div className="tpo-model-select" data-field-error="goodsModels">
      <div className="tpo-size-chart__title-row">
        <h4 className="tpo-publish__subhead">
          {fixedUi("选择模特")}
          {required ? <RequiredMark /> : null}
        </h4>
      </div>
      {required && !selectedId ? (
        <p className="tpo-wizard__inline-hint">{fixedUi("当前类目要求填写模特信息")}</p>
      ) : null}
      {errorMessage ? <div className="tpo-field__error" role="alert">{errorMessage}</div> : null}
      {loadError ? <div className="tpo-field__error" role="alert">{loadError}</div> : null}


      {loading ? (
        <p className="tpo-wizard__inline-hint">{fixedUi("加载模特列表…")}</p>
      ) : (
        <div className="tpo-model-select__field">
          <SearchableSelect
            value={selectedId}
            onChange={onSelect}
            allowClear
            placeholder={officialMode
              ? `从官方${lockedModelTypeName}中选择`
              : `从店铺${lockedModelTypeName}中选择`}
            emptyText={options.length ? '无匹配模特' : '暂无可用模特'}
            options={options}
          />
          {!options.length ? (
            <p className="tpo-wizard__inline-hint">
              {officialMode
                ? `暂无官方${lockedModelTypeName}。请先到 Admin「官方数据 · 模特库」维护。`
                : `暂无${lockedModelTypeName}。请先到「模板库 · 模特库」新建，或点击「拉取 TEMU 后台数据」。`}
            </p>
          ) : null}
          {!officialMode && selectedReq && selectedId && !catalogById.has(String(selectedId)) ? (
            <p className="tpo-wizard__inline-hint">
              已带入官方模特资料。发布到店铺前请改选本店模特。
            </p>
          ) : null}
        </div>
      )}

      {selectedReq || selectedRow ? (
        <div className="tpo-model-select__card" aria-live="polite">
          <div className="tpo-model-select__card-media">
            {cardThumb ? (
              <img src={cardThumb} alt="" width={72} height={72} />
            ) : (
              <span className="tpo-model-select__card-placeholder" aria-hidden="true">{fixedUi("模特")}</span>
            )}
          </div>
          <div className="tpo-model-select__card-body">
            <div className="tpo-model-select__card-title">
              <strong>{cardName || fixedUi("已选模特")}</strong>
              {cardType ? <span className="tpo-model-select__card-tag">{cardType}</span> : null}
            </div>
            {cardMeasures.length ? (
              <ul className="tpo-model-select__card-metrics">
                {cardMeasures.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>
            ) : (
              <p className="tpo-wizard__inline-hint">{fixedUi("暂无量体信息")}</p>
            )}
            {normalizedSizeOptions.length ? (
              <div className="tpo-model-select__tryon">
                <div className="tpo-model-select__tryon-item">
                  <span className="tpo-field__label">{fixedUi("试穿尺码")}</span>
                  <div className="tpo-model-select__tryon-control">
                    <SearchableSelect
                      value={tryOnSizeValue}
                      onChange={onTryOnSizeChange}
                      allowClear={false}
                      placeholder={fixedUi("请选择商品尺码")}
                      options={sizeSelectOptions}
                    />
                  </div>
                </div>
                <div className="tpo-model-select__tryon-item">
                  <span className="tpo-field__label">{fixedUi("试穿感受")}</span>
                  <div className="tpo-model-select__tryon-control tpo-model-select__tryon-control--feel">
                    <SearchableSelect
                      value={tryOnFeelValue}
                      onChange={onTryOnResultChange}
                      allowClear={false}
                      placeholder={fixedUi("合身")}
                      options={tryOnFeelOptions}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <p className="tpo-wizard__inline-hint">{fixedUi("请先在规格供应中选择尺码，再指定模特试穿尺码。")}</p>
            )}
            {(selectedReq?.sizeSpecName || selectedReq?.modelId || tryOnFeelLabel) ? (
              <p className="tpo-model-select__card-meta">
                {[
                  selectedReq?.modelId ? `ID ${selectedReq.modelId}` : '',
                  selectedReq?.sizeSpecName ? `试穿 ${selectedReq.sizeSpecName}` : '',
                  tryOnFeelLabel ? `感受 ${tryOnFeelLabel}` : '',
                ].filter(Boolean).join(' · ')}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="tpo-model-select__card-clear"
            onClick={() => onChange?.([])}
          >
            {fixedUi("清除")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
